import { useEffect, useState } from "react" ;
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");

/* eslint-disable-next-line react-hooks-exhaustive-deps */
useEffect(() => {
  if (!sessionId) {
    navigate("/payment");
    return;
  }

  pollPaymentStatus();
}, []);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === "paid") {
        setStatus("success");
        
        // Update user data
        const userResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(userResponse.data);
        localStorage.setItem("user", JSON.stringify(userResponse.data));
        
        toast.success("Payment successful! You're now a premium member.");
        return;
      } else if (response.data.status === "expired") {
        setStatus("failed");
        return;
      }

      // Continue polling
      setAttempts((prev) => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment:", error);
      setAttempts((prev) => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  if (status === "checking") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-black border-t-[#CCFF00] animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-bold mb-2">Processing Payment...</h2>
          <p className="text-gray-500">Please wait while we confirm your payment.</p>
        </motion.div>
      </div>
    );
  }

  if (status === "failed" || status === "timeout") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-red-100 border-2 border-red-500 mx-auto mb-6 flex items-center justify-center">
            <span className="text-4xl">✕</span>
          </div>
          <h2 className="text-3xl font-extrabold tracking-tighter mb-4">
            Payment {status === "timeout" ? "Timeout" : "Failed"}
          </h2>
          <p className="text-gray-500 mb-8">
            {status === "timeout"
              ? "We couldn't confirm your payment. Please check your email for confirmation."
              : "There was an issue with your payment. Please try again."}
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => navigate("/payment")}
              className="bg-black text-white hover:bg-[#CCFF00] hover:text-black border-2 border-black font-bold"
            >
              Try Again
            </Button>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="border-2 border-black font-bold"
            >
              Go Home
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className="w-24 h-24 bg-[#CCFF00] border-4 border-black mx-auto mb-6 flex items-center justify-center"
        >
          <Check className="w-12 h-12" />
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h1 className="text-4xl font-extrabold tracking-tighter mb-4">
            Welcome to Premium!
          </h1>
          <p className="text-gray-500 mb-8">
            Your payment was successful. You now have access to all premium features
            including sponsored posts, top placement, and cross-platform posting.
          </p>

          <div className="flex flex-col gap-3">
            <Button
              onClick={() => navigate("/upload")}
              data-testid="create-sponsored-btn"
              className="bg-[#FF0099] text-white hover:bg-[#CCFF00] hover:text-black border-2 border-black font-bold py-3"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Create Sponsored Content
            </Button>
            <Button
              onClick={() => navigate("/profile")}
              variant="outline"
              className="border-2 border-black font-bold py-3"
            >
              Go to Profile
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </motion.div>

        {/* Confetti Effect */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ 
                y: -20, 
                x: Math.random() * window.innerWidth,
                rotate: 0
              }}
              animate={{ 
                y: window.innerHeight + 20,
                rotate: 360 * (Math.random() > 0.5 ? 1 : -1)
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                delay: Math.random() * 0.5,
                ease: "linear"
              }}
              className="absolute w-3 h-3"
              style={{
                backgroundColor: ["#CCFF00", "#FF0099", "#7C3AED", "#000"][Math.floor(Math.random() * 4)]
              }}
            />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
